import { useLocation, useNavigate } from 'react-router-dom';
import { useMemo } from 'react';

type InferType<P> = P extends Type<infer R> ? R : never;
type InferValue<P> = P extends Record<string, infer R> ? R : never;

type Type<T> = {
  serialize(value: T): string | null;
  deserialize(value: string | null): T;
  map<U>(to: (value: T) => U, from: (value: U) => T): Type<U>;
  filter(predicate: (value: T) => boolean): Type<T | null>;
  delimited(delimiter: string): Type<NonNullable<T>[]>;
  oneOf<U extends T>(variants: U[]): Type<U | null>;
  defaultTo(defaultValue: NonNullable<T>): Type<NonNullable<T>>;
};

function makeType<T>(
  serialize: Type<T>['serialize'],
  deserialize: Type<T>['deserialize'],
): Type<T> {
  return {
    serialize,
    deserialize,

    map<U>(to: (value: T) => U, from: (value: U) => T): Type<U> {
      return makeType(
        (u) => serialize(from(u)),
        (s) => to(deserialize(s)),
      );
    },

    filter(predicate): Type<T | null> {
      return makeType(
        (value) => (value === null ? null : serialize(value)),
        (s) => {
          const res = deserialize(s);

          return predicate(res) ? res : null;
        },
      );
    },

    delimited(delimiter): Type<NonNullable<T>[]> {
      return makeType(
        (values) =>
          values
            .values()
            .map((v) => serialize(v))
            .filter((s): s is string => s !== null)
            .map(encodeURIComponent)
            .reduce<string>(
              (acc, s) => (acc.length === 0 ? s : acc + delimiter + s),
              '',
            ),
        (s) => {
          if (s === null) {
            return [];
          }

          return s
            .split(delimiter)
            .values()
            .map((p) => p.trim())
            .filter((p) => p.length > 0)
            .map((p) => deserialize(decodeURIComponent(p)))
            .filter((v): v is NonNullable<T> => v !== null && v !== undefined)
            .toArray();
        },
      );
    },

    oneOf<U extends T>(variants: U[]): Type<U | null> {
      return makeType(
        (value) => (value === null ? null : serialize(value)),
        (s) => {
          const res = deserialize(s) as U;

          return variants.includes(res) ? res : null;
        },
      );
    },

    defaultTo(defaultValue): Type<NonNullable<T>> {
      return makeType(
        (value) => serialize(value),
        (s) => {
          const res = deserialize(s);

          return res === null ? defaultValue : res!;
        },
      );
    },
  };
}

export const ParamTypes = {
  string: (() => {
    const type = makeType<string | null>(
      (v) => v,
      (v) => v,
    );

    return () => type;
  })(),

  number: (() => {
    const type = makeType<number | null>(
      (v) => (v === null ? null : String(v)),
      (v) => {
        const n = Number(v);

        return Number.isFinite(n) ? n : null;
      },
    );

    return () => type;
  })(),

  integer: (() => {
    const type = makeType<number | null>(
      (v) => (v === null ? null : String(v)),
      (v) => {
        const n = Number(v);

        return Number.isFinite(n) ? n : null;
      },
    ).filter((n) => Number.isInteger(n));

    return () => type;
  })(),

  boolean: (() => {
    const type = makeType<boolean | null>(
      (v) => {
        if (v === null) {
          return null;
        }

        return v ? '1' : '0';
      },
      (v) => {
        if (v === null) {
          return null;
        }

        switch (v.toLowerCase()) {
          case 'true':
          case '1':
            return true;
          case 'false':
          case '0':
            return false;
          default:
            return null;
        }
      },
    );

    return () => type;
  })(),

  date: (() => {
    const type = makeType<Date | null>(
      (v) => (v === null ? null : v.toISOString()),
      (v) => {
        if (v === null) {
          return null;
        }

        const ts = Date.parse(v);

        return Number.isNaN(ts) ? null : new Date(ts);
      },
    );

    return () => type;
  })(),

  // eslint-disable-next-line
  enum<T extends Record<string, any>>(variants: T): Type<InferValue<T> | null> {
    return makeType(
      (value) => {
        if (value === null) {
          return null;
        }

        const entry = Object.entries(variants).find(([, v]) => v === value);

        return entry ? entry[0] : null;
      },
      (raw) => {
        if (raw === null) {
          return null;
        }

        const entry = Object.entries(variants).find(([k]) => k === raw);

        return entry ? entry[1] : null;
      },
    );
  },

  array<T>(elementType: Type<T>): Type<NonNullable<T>[]> {
    return elementType.delimited(',');
  },

  // eslint-disable-next-line
  object<T extends Record<string, Type<any>>>(params: T) {
    type Obj = { [K in keyof T]: InferType<T[K]> };

    return makeType<Obj | null>(
      (value) => {
        if (value === null) {
          return null;
        }

        const result: Record<string, string> = {};

        for (const [key, type] of Object.entries(params)) {
          const v = value[key] as InferType<(typeof params)[typeof key]>;
          const serialized = type.serialize(v);

          if (serialized !== null) {
            result[key] = serialized;
          }
        }

        return JSON.stringify(result);
      },
      (raw) => {
        if (raw === null) {
          return null;
        }

        let parsed: unknown;
        try {
          parsed = JSON.parse(raw);
        } catch {
          return null;
        }

        if (
          typeof parsed !== 'object' ||
          parsed === null ||
          Array.isArray(parsed)
        ) {
          return null;
        }

        const source = parsed as Record<string, unknown>;
        const result: Record<string, unknown> = {};

        for (const [key, type] of Object.entries(params)) {
          const rawValue = source[key];

          if (typeof rawValue === 'string') {
            result[key] = type.deserialize(rawValue);
          } else {
            result[key] = type.deserialize(null);
          }
        }

        return result as Obj;
      },
    );
  },
};

// eslint-disable-next-line
export type QueryParams<T extends Record<string, any>> = {
  /**
   * The parsed query parameter values by key.
   */
  readonly params: Readonly<{ [K in keyof T]: InferType<T[K]> | null }>;

  /**
   * Sets the value of the given query parameter key. Passing a value of `null` will remove
   * the query parameter.
   *
   * Note that this will replace the current React Router history entry, unless `silent` is
   * set to `false` in which case a new entry is pushed onto the React Router history stack.
   * State is always persisted.
   */
  setParam<K extends keyof T>(
    key: K,
    value: InferType<T[K]> | null,
    silent?: boolean,
  ): void;
};

/**
 * A hook used for reading and updating query parameters.
 *
 * This hook will react to changes to the React Router history.
 */
// eslint-disable-next-line
export function useQueryParams<T extends Record<string, Type<any>>>(
  types: T,
): QueryParams<T> {
  const navigate = useNavigate();
  const location = useLocation();

  return useMemo(() => {
    const params = new URLSearchParams(location.search);

    return {
      params: Object.fromEntries(
        Object.entries(types).map(([key, type]) => {
          if (params.has(key)) {
            const value = params.get(key);
            const deserialized = type.deserialize(value);

            return [key, deserialized];
          } else {
            return [key, null];
          }
        }),
      ) as Readonly<{ [K in keyof T]: InferType<T[K]> | null }>,
      setParam(key, value, silent = true) {
        const params = new URLSearchParams(location.search);
        const type = types[key as keyof T];
        const string = type.serialize(value);

        if (string === null || string === '') {
          params.delete(key as string);
        } else {
          params.set(key as string, string);
        }

        const descriptor = {
          pathname: location.pathname,
          search: '?' + params,
        };

        if (silent) {
          navigate(descriptor, { replace: true, state: location.state });
        } else {
          navigate(descriptor, { state: location.state });
        }
      },
    };
  }, [location, navigate]);
}
